import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { Observable, of, Subject } from 'rxjs';
import { ClientsService, DocumentsService, SignedDocumentsService } from 'src/app/api/services';
import { Client, ClientRepresentative, ClientDocument, ClientDocumentType } from 'src/app/api/models';
import { SignDocumentRequestDialogComponent } from 'src/app/components/sign-document-request-dialog/sign-document-request-dialog.component';
import { CountriesMap, CountriesMapCache } from 'src/app/model/model-map-cache';
import { ClientPeerService } from '../../service/client-peer.service';
import { shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';
import { ClientEditDialogComponent } from 'src/app/components/client-edit-dialog/client-edit-dialog.component';


// This page is for CompanyClient. A new page will be needed for LightEntrepreneurs.
@Component({
  selector: 'app-client-page',
  templateUrl: './client-page.component.html',
  styleUrls: ['./client-page.component.scss'],
  providers: [ClientPeerService]
})
export class ClientPageComponent implements OnInit, OnDestroy {
  private readonly ngDestroy$ = new Subject();

  SigningStatus = Object.freeze({ UNSIGNED: 1, PENDING: 2, SIGNED: 3, FAILED: 4 })

  countries$: Observable<CountriesMap>;
  client$: Observable<Client>;

  representatives$: Observable<ClientRepresentative[]>;
  representativesColumns = ['first_name', 'last_name', 'email', 'phone', 'created_at', 'actions'];

  documents$: Observable<ClientDocument[]>;
  documentTypes$: Observable<ClientDocumentType[]>;

  constructor(private clientPeerService: ClientPeerService,
    private documentsService: DocumentsService,
    private clientsService: ClientsService,
    private signedDocumentsService: SignedDocumentsService,
    private countriesMapCache: CountriesMapCache,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router) {
  }

  dateToString(date: Date): string {
    return date.toISOString();
  }

  openEditDialog(): void {
    this.client$.pipe(take(1)).subscribe(client => {
      const dialogRef = this.dialog.open(ClientEditDialogComponent, {
        width: '800px',
        data: { client: client }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.clientPeerService.refresh();
        }
      });
    });
  }

  downloadClientDocument(doc: ClientDocument): void {
    if (doc.signed_document && doc.signed_document.status === 'signed') {
      this.downloadSignedClientDocument(doc);
    } else {
      this.downloadOriginalClientDocument(doc);
    }
  }

  downloadOriginalClientDocument(doc: ClientDocument): void {
    this.documentsService.getClientDocumentUrl(doc).subscribe(fileUrl => {
      console.log(fileUrl);
      window.open(fileUrl.url, '_blank');
    });
  }

  downloadSignedClientDocument(doc: ClientDocument): void {
    this.signedDocumentsService.getSignedDocumentUrl(doc.signed_document).subscribe(fileUrl => {
      console.log(fileUrl);
      window.open(fileUrl.url, '_blank');
    });
  }

  clientDocumentFileAdded(client: Client, file: File): void {
    console.log("file", file);
    this.documentsService.createClientDocument({
      client_id: client.id,
      body: {
        file: file,
      },
    }).pipe(take(1))
      .subscribe(() => {
        this.clientPeerService.refresh();
      });
  }

  confirmDeleteClientDocument(doc: ClientDocument): void {
    console.log("confirm", doc);
    const dialogData = new ConfirmationDialogModel('Delete Confirmation', `Are you sure you want to delete the ${doc.type_description} document ${doc.filename}?`);
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.documentsService.deleteClientDocument(doc)
          .pipe(take(1))
          .subscribe(() => {
            this.clientPeerService.refresh();
          });
      }
    });
  }

  documentSigningStatus(doc: ClientDocument): number {
    if (!doc.signed_document) {
      return this.SigningStatus.UNSIGNED;
    }
    if (doc.signed_document?.status === 'signed') {
      return this.SigningStatus.SIGNED;
    }
    if (doc.signed_document?.status === 'cancelled' || doc.signed_document?.status === 'deleted') {
      return this.SigningStatus.FAILED;
    }
    return this.SigningStatus.PENDING
  }

  canSendSignal(doc: ClientDocument): boolean {
    return this.documentSigningStatus(doc) === this.SigningStatus.PENDING
  }

  remindSigners(doc: ClientDocument): void {
    console.log("remind", doc);
    const dialogData = new ConfirmationDialogModel('Remind Confirmation', `Are you sure you want to remind signers to sign the '${doc.name}' (${doc.filename})?`);
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.signedDocumentsService.remindInvitees(doc.signed_document)
          .pipe(take(1))
          .subscribe(() => {
            this.clientPeerService.refresh();
          });
      }
    });
  }

  cancelDocumentSigning(doc: ClientDocument): void {
    console.log("remind", doc);
    const dialogData = new ConfirmationDialogModel('Cancel Signing Confirmation', `Are you sure you want to cancel the signing process for '${doc.name}' (${doc.filename})?`);
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.signedDocumentsService.cancelSigning(doc.signed_document)
          .pipe(take(1))
          .subscribe(() => {
            this.clientPeerService.refresh();
          });
      }
    });
  }

  changeClientDocumentType(doc: ClientDocument, docType: ClientDocumentType): void {
    doc.type_id = docType.id;

    this.documentsService.updateClientDocument({
      client_id: doc.client_id,
      id: doc.id,
      body: doc,
    }).pipe(take(1))
      .subscribe(() => {
        this.clientPeerService.refresh();
      });
  }

  openSignDocumentRequestDialog(doc: ClientDocument): void {
    console.log(doc);
    const dialogRef = this.dialog.open(SignDocumentRequestDialogComponent, {
      //width: '250px',
      data: { document: doc },
    });

    // TODO really neccessary?
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.clientPeerService.refresh();
      }
    });
  }

  ngOnInit() {
    this.countries$ = this.countriesMapCache.map$;
    this.documentTypes$ = this.documentsService.getClientDocumentTypes();

    this.client$ = this.clientPeerService.get().pipe(shareReplay(1));

    this.documents$ = this.client$
      .pipe(
        switchMap(client => {
          if (client && client.id > 0) {
            return this.documentsService.getClientDocuments({ client_id: client.id })
          } else {
            return of([]);
          }
        }),
      );

    this.representatives$ = this.client$
      .pipe(
        switchMap(client => {
          if (client && client.id > 0) {
            return this.clientsService.getClientRepresentatives({ client_id: client.id })
          } else {
            return of([]);
          }
        }),
      );

    this.route.paramMap.pipe(takeUntil(this.ngDestroy$))
      .subscribe((params: ParamMap) => {
        if (params.has('clientId')) {
          this.clientPeerService.initialize(+params.get('clientId'))
        } else {
          this.clientPeerService.create();
        }
      });
  }

  ngOnDestroy() {
    this.ngDestroy$.next(undefined);
    this.ngDestroy$.complete();
    /*
    if (this.representatives) {
      this.representatives.disconnect();
      this.representatives = null;
    }
    */
  }
}
